import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RequireStaff } from "@/components/RequireStaff";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import {
  doc, getDoc, updateDoc, getDocs, collection, query, where, orderBy,
  addDoc, serverTimestamp,
} from "firebase/firestore";
import { db, storage } from "@/lib/firebase";
import { ref, listAll, uploadBytes, getDownloadURL } from "firebase/storage";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, FileText, Building2, Send, Eye, ChevronRight,
  Loader2, ArrowRight, Rocket, BarChart3, ClipboardList, Landmark,
  ClipboardEdit, Calculator, FolderLock, Check, EyeOff, RefreshCw,
} from "lucide-react";
import { StaffDscrEvaluator } from "@/components/staff/StaffDscrEvaluator";

// ── Noms d'étapes alignés sur DealStage (create-deal.dto.ts, backend) ──────
type DealStage =
  | "submission" | "pre_evaluation" | "cim_creation" | "payment_pending"
  | "published" | "lender_submission" | "nda_signed" | "buyer_contact"
  | "first_meeting" | "loi_signed" | "closing";

const STAGE_LABELS: Record<DealStage, { en: string; fr: string }> = {
  submission: { en: "Submission", fr: "Soumission" },
  pre_evaluation: { en: "Pre-Evaluation", fr: "Pré-évaluation" },
  cim_creation: { en: "CIM Creation", fr: "Création du CIM" },
  payment_pending: { en: "Payment Pending", fr: "Paiement en attente" },
  published: { en: "Published", fr: "Publié" },
  lender_submission: { en: "Lender Submission", fr: "Soumission Prêteurs" },
  nda_signed: { en: "NDA Signed", fr: "NDA Signé" },
  buyer_contact: { en: "Buyer Contact", fr: "Contact Acheteur" },
  first_meeting: { en: "First Meeting", fr: "Première Rencontre" },
  loi_signed: { en: "LOI Signed", fr: "LOI Signé" },
  closing: { en: "Closing", fr: "Closing" },
};
const STAGE_ORDER: DealStage[] = [
  "submission", "pre_evaluation", "cim_creation", "payment_pending",
  "published", "lender_submission", "nda_signed", "buyer_contact",
  "first_meeting", "loi_signed", "closing",
];

function fmt(n?: number, currency = "CAD") {
  return n ? new Intl.NumberFormat("fr-CA", { style: "currency", currency }).format(n) : "—";
}

function yn(v: any, lang: "en" | "fr") {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "boolean") return v ? (lang === "fr" ? "Oui" : "Yes") : (lang === "fr" ? "Non" : "No");
  return String(v);
}

function GenericItemList({ items }: { items: any[] }) {
  if (!Array.isArray(items) || items.length === 0) return <p className="text-sm text-gray-500">—</p>;
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="border-b border-gray-700 pb-2 last:border-b-0">
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(item || {}).map(([k, v]) => (
              <div key={k}>
                <span className="text-gray-500">{k}: </span>
                <span className="text-gray-200">{typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function StaffDealDetail() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();
  const lang = (language === "fr" ? "fr" : "en") as "en" | "fr";

  const [loading, setLoading] = useState(true);
  const [dealRoot, setDealRoot] = useState<any>(null);
  const [dealCollection, setDealCollection] = useState<"deals" | "dealSubmissions">("dealSubmissions");
  const [generatedDocs, setGeneratedDocs] = useState<any[]>([]);
  const [generating, setGenerating] = useState<"cim" | null>(null);
  const [stageChanging, setStageChanging] = useState(false);

  // ── AJOUTÉ : contrôle manuel staff de la visibilité sur le Deal Monitor
  // (filet de sécurité si l'automatisation via onApprovalFeePaid() échoue
  // ou si currentStage est inattendu). N'affecte jamais le pipeline
  // automatique lui-même — vient s'additionner via listingOverride,
  // vérifié en plus de currentStage dans Listings.tsx et firestore.rules.
  const [overrideChanging, setOverrideChanging] = useState(false);

  // ── AJOUTÉ : relance manuelle du matching prêteur (filet de sécurité si
  // LenderMatchingService.onDealApprovalPaid() a échoué silencieusement
  // après le paiement — voir deals.service.ts, catch de triggerLenderMatching).
  const [retryingMatching, setRetryingMatching] = useState(false);

  // ── Gestion du Deal Room ──────────────────────────────────────────────
  const [dealRoomCount, setDealRoomCount] = useState(0);
  const [dealRoomPaths, setDealRoomPaths] = useState<Set<string>>(new Set());
  const [pushingDoc, setPushingDoc] = useState<string | null>(null);

  useEffect(() => { if (dealId) loadAll(); }, [dealId]);
  useEffect(() => { if (dealId) loadDealRoomState(); }, [dealId]);

  const loadDealRoomState = async () => {
    if (!dealId) return;
    try {
      const roomRef = ref(storage, `deal-room/${dealId}`);
      const listing = await listAll(roomRef);
      setDealRoomCount(listing.items.length);
      setDealRoomPaths(new Set(listing.items.map(i => i.name)));
    } catch (err) {
      setDealRoomCount(0);
      setDealRoomPaths(new Set());
    }
  };

  const pushToDealRoom = async (docItem: { name?: string; url: string; mimeType?: string; type?: string }) => {
    if (!dealId) return;
    const displayName = docItem.name || "document";
    setPushingDoc(displayName);
    try {
      const res = await fetch(docItem.url);
      const blob = await res.blob();
      const safeName = displayName.replace(/[^a-zA-Z0-9._-]/g, "_");
      const destRef = ref(storage, `deal-room/${dealId}/${safeName}`);
      await uploadBytes(destRef, blob, {
        contentType: docItem.mimeType || docItem.type || blob.type,
        customMetadata: { uploadedBy: "staff" },
      });
      await getDownloadURL(destRef);
      setDealRoomCount(c => c + 1);
      setDealRoomPaths(prev => new Set(prev).add(safeName));
      toast({
        title: lang === "fr" ? "Ajouté au Deal Room" : "Added to Deal Room",
        description: displayName,
      });
    } catch (err) {
      console.error("[DealDetail] pushToDealRoom error:", err);
      toast({
        title: lang === "fr" ? "Erreur" : "Error",
        description: lang === "fr" ? "Échec de l'ajout au Deal Room." : "Failed to add to Deal Room.",
        variant: "destructive",
      });
    } finally {
      setPushingDoc(null);
    }
  };

  const loadAll = async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      let snap = await getDoc(doc(db, "dealSubmissions", dealId));
      let col: "deals" | "dealSubmissions" = "dealSubmissions";
      if (!snap.exists()) {
        snap = await getDoc(doc(db, "deals", dealId));
        col = "deals";
      }
      if (snap.exists()) {
        setDealRoot(snap.data());
        setDealCollection(col);
      }

      const docsSnap = await getDocs(
        query(collection(db, "dealDocuments"), where("deal_id", "==", dealId), orderBy("createdAt", "desc"))
      );
      setGeneratedDocs(docsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("[DealDetail] load error:", err);
      toast({ title: lang === "fr" ? "Erreur de chargement" : "Load error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const raw = dealRoot?.rawFormData || {};
  const general = raw.general || {};
  const operations = raw.operations || {};
  const financials = raw.financials || {};
  const assets = raw.assets || {};
  const realEstate = raw.realEstate || {};
  const addbacks = raw.addbacks || {};
  const projections = raw.projections || {};
  const culture = raw.culture || {};
  const swot = raw.swot || {};
  const licensing = raw.licensing || {};
  const impact = raw.impact || {};

  const addbackItems: { description: string; amount: number }[] = addbacks.items || [];
  const addbacksTotal = addbackItems.reduce((sum: number, it: any) => sum + (Number(it?.amount) || 0), 0);

  const businessName = general.legalName || dealRoot?.legalName || dealRoot?.businessName || "Untitled Deal";
  const askingPrice = financials.includeRealEstateInPrice
    ? (financials.totalAskingPriceWithRealEstate || financials.askingPrice)
    : (financials.askingPrice || dealRoot?.askingPrice);
  const referenceNumber = dealRoot?.referenceNumber;
  const currentStage: DealStage = dealRoot?.currentStage || dealRoot?.current_stage || dealRoot?.stage || "submission";
  const currentIdx = STAGE_ORDER.indexOf(currentStage);
  const uploadedDocuments: any[] = dealRoot?.uploadedDocuments || [];

  // ── AJOUTÉ : état de visibilité manuelle actuel, pour affichage/logique bouton
  const listingOverride: "show" | "hide" | null = dealRoot?.listingOverride || null;

  // ── Actions ──────────────────────────────────────────────
  const generateCIM = async () => {
    setGenerating("cim");
    try {
      const { postApi } = await import("@/lib/api");
      const result = await postApi("/ai/generate-cim", { dealId, language: lang });
      if (!result?.success) throw new Error("CIM generation failed");
      toast({ title: lang === "fr" ? "CIM généré!" : "CIM generated!" });
    } catch (err: any) {
      toast({ title: lang === "fr" ? "Erreur" : "Error", description: err.message, variant: "destructive" });
    } finally {
      setGenerating(null);
    }
  };

  const changeStage = async (newStage: DealStage) => {
    if (!dealId) return;
    setStageChanging(true);
    try {
      const payload = {
        current_stage: newStage, currentStage: newStage, stage: newStage,
        updatedAt: serverTimestamp(), updated_at: serverTimestamp(),
      };
      const targets = ["deals", "dealSubmissions"] as const;
      const results = await Promise.allSettled(
        targets.map(async (col) => {
          const ref = doc(db, col, dealId);
          const snap = await getDoc(ref);
          if (!snap.exists()) return false;
          await updateDoc(ref, payload);
          return true;
        })
      );
      const updated = results.some(r => r.status === "fulfilled" && r.value === true);
      if (!updated) throw new Error("Deal introuvable");
      setDealRoot((prev: any) => ({ ...prev, currentStage: newStage, current_stage: newStage, stage: newStage }));
      toast({ title: lang === "fr" ? "Étape mise à jour" : "Stage updated", description: STAGE_LABELS[newStage][lang] });
    } catch (err: any) {
      toast({ title: lang === "fr" ? "Erreur" : "Error", description: err.message, variant: "destructive" });
    } finally {
      setStageChanging(false);
    }
  };

  // ── AJOUTÉ : bascule manuelle de visibilité (filet de sécurité, ne
  // remplace jamais l'automatisation onApprovalFeePaid()). Écrit
  // listingOverride sur 'deals' ET 'dealSubmissions' pour cohérence,
  // sur le même modèle que changeStage().
  const setListingOverride = async (value: "show" | "hide" | null) => {
    if (!dealId) return;
    setOverrideChanging(true);
    try {
      const targets = ["deals", "dealSubmissions"] as const;
      await Promise.allSettled(
        targets.map(async (col) => {
          const ref = doc(db, col, dealId);
          const snap = await getDoc(ref);
          if (!snap.exists()) return;
          await updateDoc(ref, { listingOverride: value, updatedAt: serverTimestamp() });
        })
      );
      setDealRoot((prev: any) => ({ ...prev, listingOverride: value }));
      toast({
        title: lang === "fr" ? "Visibilité mise à jour" : "Visibility updated",
        description: value === "show"
          ? (lang === "fr" ? "Deal forcé visible sur le Deal Monitor" : "Deal forced visible on Deal Monitor")
          : value === "hide"
            ? (lang === "fr" ? "Deal masqué du Deal Monitor" : "Deal hidden from Deal Monitor")
            : (lang === "fr" ? "Visibilité automatique restaurée" : "Automatic visibility restored"),
      });
    } catch (err: any) {
      toast({ title: lang === "fr" ? "Erreur" : "Error", description: err.message, variant: "destructive" });
    } finally {
      setOverrideChanging(false);
    }
  };

  // ── AJOUTÉ : relance manuelle du matching prêteur — appelle une route
  // NestJS à créer (voir note ci-dessous), qui ré-exécute
  // LenderMatchingService.onDealApprovalPaid() pour ce deal sans repasser
  // par tout le webhook Stripe. Utile si le matching a échoué la première
  // fois (deal resté bloqué à PUBLISHED, comme documenté dans le catch de
  // triggerLenderMatching() côté deals.service.ts).
  const retryLenderMatching = async () => {
    if (!dealId) return;
    setRetryingMatching(true);
    try {
      const { postApi } = await import("@/lib/api");
      const result = await postApi(`/deals/${dealId}/retry-lender-matching`, {});
      toast({
        title: lang === "fr" ? "Matching relancé" : "Matching retried",
        description: lang === "fr"
          ? `${result?.matchedCount ?? 0} prêteur(s) notifié(s)`
          : `${result?.matchedCount ?? 0} lender(s) notified`,
      });
    } catch (err: any) {
      toast({ title: lang === "fr" ? "Erreur" : "Error", description: err.message, variant: "destructive" });
    } finally {
      setRetryingMatching(false);
    }
  };

  const ndaUrl = `/tools/nda-generator?deal=${dealId}&dealName=${encodeURIComponent(businessName)}&industry=${encodeURIComponent(general.industry || "")}&recipientType=buyer`;
  const marketStudyUrl = `/staff/ai/market-study?deal=${dealId}`;
  const strategicPlanUrl = `/staff/ai/strategic-plan?deal=${dealId}`;
  const cimReviewUrl = `/admin/cim-review/${dealId}`;

  const actionBtnClass =
    "h-16 flex-col gap-1.5 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white";

  const sectionCardClass = "bg-gray-800/50 rounded-xl p-4";
  const sectionTitleClass = "text-sm font-semibold text-gray-300 mb-3";
  const fieldGridClass = "grid grid-cols-2 gap-3 text-sm text-gray-200";

  if (loading) {
    return (
      <RequireStaff>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RequireStaff>
    );
  }

  if (!dealRoot) {
    return (
      <RequireStaff>
        <div className="p-8 text-center text-muted-foreground">
          {lang === "fr" ? "Dossier introuvable." : "Deal not found."}
        </div>
      </RequireStaff>
    );
  }

  return (
    <RequireStaff>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/staff/my-work")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {lang === "fr" ? "Retour" : "Back"}
          </Button>
          <div className="flex items-center gap-2">
            {listingOverride === "show" && (
              <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                {lang === "fr" ? "Forcé visible" : "Forced visible"}
              </Badge>
            )}
            {listingOverride === "hide" && (
              <Badge variant="outline" className="border-red-500/50 text-red-400">
                {lang === "fr" ? "Masqué manuellement" : "Manually hidden"}
              </Badge>
            )}
            <Badge variant="outline">{STAGE_LABELS[currentStage]?.[lang] || currentStage}</Badge>
          </div>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-white">{businessName}</h1>
          <p className="text-sm text-gray-400 mt-1">
            {referenceNumber || dealId?.slice(0, 8)} · {fmt(askingPrice)}
          </p>
        </div>

        {/* Stage progress */}
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {STAGE_ORDER.map((stage, idx) => (
            <div key={stage} className="flex items-center flex-shrink-0">
              <button
                onClick={() => changeStage(stage)}
                disabled={stageChanging}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  idx < currentIdx ? "bg-primary/20 text-primary" :
                  idx === currentIdx ? "bg-primary text-primary-foreground" :
                  "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {STAGE_LABELS[stage][lang]}
              </button>
              {idx < STAGE_ORDER.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground mx-0.5" />}
            </div>
          ))}
        </div>

        {/* PRÉ-ÉVALUATION — DSCR */}
        <div className="bg-gray-800/50 rounded-xl p-4 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="h-4 w-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
              {lang === "fr" ? "Pré-évaluation — Ratio de couverture de la dette (DSCR)" : "Pre-Evaluation — Debt Service Coverage Ratio (DSCR)"}
            </h2>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            {lang === "fr"
              ? "À compléter avant de générer le CIM. Objectif : DSCR ≥ 1,3x. Ajustez le salaire du propriétaire, l'immobilier et les actifs ci-dessous pour tester différents scénarios."
              : "Complete before generating the CIM. Target: DSCR ≥ 1.3x. Adjust owner salary, real estate, and assets below to test different scenarios."}
          </p>
          {dealId && <StaffDscrEvaluator dealId={dealId} language={lang} />}
        </div>

        {/* Actions */}
        <div className="bg-gray-800/50 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide mb-3">
            {lang === "fr" ? "Actions" : "Actions"}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button onClick={generateCIM} disabled={!!generating} variant="outline" className={actionBtnClass}>
              {generating === "cim" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
              <span className="text-xs">{lang === "fr" ? "Générer CIM" : "Generate CIM"}</span>
            </Button>
            <Button
              onClick={() => navigate(cimReviewUrl)}
              variant="outline" className={actionBtnClass}
            >
              <ClipboardEdit className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Réviser le CIM" : "Review CIM"}</span>
            </Button>
            <Button
              onClick={() => window.open(`/cim-preview/${dealId}`, "_blank", "noopener,noreferrer")}
              variant="outline" className={actionBtnClass}
            >
              <Eye className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Voir le CIM" : "View CIM"}</span>
            </Button>
            <Button
              onClick={() => window.open(`/deal-room/${dealId}`, "_blank", "noopener,noreferrer")}
              variant="outline" className={actionBtnClass}
            >
              <FolderLock className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Voir le Deal Room" : "View Deal Room"}</span>
            </Button>
            <Button
              onClick={() => navigate(ndaUrl)}
              variant="outline" className={actionBtnClass}
            >
              <FileText className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Générer NDA" : "Generate NDA"}</span>
            </Button>
            <Button
              onClick={() => navigate(marketStudyUrl)}
              variant="outline" className={actionBtnClass}
            >
              <BarChart3 className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Étude de marché" : "Market Study"}</span>
            </Button>
            <Button
              onClick={() => navigate(strategicPlanUrl)}
              variant="outline" className={actionBtnClass}
            >
              <Rocket className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Plan stratégique" : "Strategic Plan"}</span>
            </Button>
            <Button
              onClick={() => navigate("/listings")}
              variant="outline" className={actionBtnClass}
            >
              <ClipboardList className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Deal Monitor" : "Deal Monitor"}</span>
            </Button>

            {/* AJOUTÉ : filet de sécurité visibilité manuelle */}
            <Button
              onClick={() => setListingOverride(listingOverride === "show" ? null : "show")}
              disabled={overrideChanging}
              variant="outline" className={actionBtnClass}
            >
              {overrideChanging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              <span className="text-xs">
                {listingOverride === "show"
                  ? (lang === "fr" ? "Retirer le forçage" : "Remove force-show")
                  : (lang === "fr" ? "Forcer sur Deal Monitor" : "Force on Deal Monitor")}
              </span>
            </Button>
            <Button
              onClick={() => setListingOverride(listingOverride === "hide" ? null : "hide")}
              disabled={overrideChanging}
              variant="outline" className={actionBtnClass}
            >
              {overrideChanging ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />}
              <span className="text-xs">
                {listingOverride === "hide"
                  ? (lang === "fr" ? "Réafficher automatiquement" : "Restore automatic visibility")
                  : (lang === "fr" ? "Masquer du Deal Monitor" : "Hide from Deal Monitor")}
              </span>
            </Button>

            {/* AJOUTÉ : filet de sécurité relance matching prêteur */}
            <Button
              onClick={retryLenderMatching}
              disabled={retryingMatching}
              variant="outline" className={actionBtnClass}
            >
              {retryingMatching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="text-xs">{lang === "fr" ? "Relancer matching prêteur" : "Retry lender matching"}</span>
            </Button>

            <Button
              onClick={() => navigate(`/forms/lender-offer?deal=${dealId}`)}
              variant="outline" className={actionBtnClass}
            >
              <Landmark className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Offre prêteur" : "Lender Offer"}</span>
            </Button>
            <Button
              onClick={() => changeStage(STAGE_ORDER[Math.min(currentIdx + 1, STAGE_ORDER.length - 1)])}
              disabled={stageChanging || currentIdx >= STAGE_ORDER.length - 1}
              variant="outline" className={actionBtnClass}
            >
              <ArrowRight className="h-4 w-4" />
              <span className="text-xs">{lang === "fr" ? "Avancer l'étape" : "Advance Stage"}</span>
            </Button>
          </div>
        </div>

        {/* Tabs: Form + Documents */}
        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">{lang === "fr" ? "Formulaire soumis" : "Submitted Form"}</TabsTrigger>
            <TabsTrigger value="documents">
              {lang === "fr" ? "Documents" : "Documents"} ({uploadedDocuments.length + generatedDocs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="form" className="space-y-4 mt-4">

            {general && Object.keys(general).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "1. Informations générales" : "1. General Information"}
                </h3>
                <div className={fieldGridClass}>
                  <div><span className="text-gray-500">Nom légal:</span> {general.legalName || "—"}</div>
                  <div><span className="text-gray-500">Nom commercial:</span> {general.tradeName || "—"}</div>
                  <div><span className="text-gray-500">Type d'entité:</span> {general.entityType || "—"}</div>
                  <div><span className="text-gray-500">Industrie:</span> {general.industry || "—"}</div>
                  <div><span className="text-gray-500">Code NAICS:</span> {general.naicsCode || "—"}</div>
                  <div><span className="text-gray-500">Année fondation:</span> {general.yearEstablished || "—"}</div>
                  <div><span className="text-gray-500">Ville/Province/Pays:</span> {[general.locationCity, general.locationState, general.locationCountry].filter(Boolean).join(", ") || "—"}</div>
                  <div><span className="text-gray-500">Site web:</span> {general.website || "—"}</div>
                  <div className="col-span-2"><span className="text-gray-500">Raison de vente:</span> {general.reasonForSale || culture.reasonForSale || "—"}</div>
                </div>
                {general.description && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{general.description}</p>
                  </div>
                )}
              </div>
            )}

            {operations && Object.keys(operations).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "2. Opérations" : "2. Operations"}
                </h3>
                <div className={fieldGridClass}>
                  <div><span className="text-gray-500">Employés temps plein:</span> {operations.fullTimeEmployees ?? "—"}</div>
                  <div><span className="text-gray-500">Employés temps partiel:</span> {operations.partTimeEmployees ?? "—"}</div>
                  <div><span className="text-gray-500">Sous-traitants:</span> {operations.subcontractors ?? "—"}</div>
                  <div><span className="text-gray-500">Heures propriétaire/sem:</span> {operations.ownerHoursPerWeek ?? "—"}</div>
                  <div><span className="text-gray-500">Salaire annuel propriétaire:</span> {fmt(operations.ownerAnnualSalary)}</div>
                  <div><span className="text-gray-500">Type propriétaire:</span> {operations.ownerType || "—"}</div>
                  <div><span className="text-gray-500">Langues:</span> {operations.languages || "—"}</div>
                  <div><span className="text-gray-500">Syndiqué:</span> {yn(operations.unionAffiliated, lang)} {operations.unionDetails ? `(${operations.unionDetails})` : ""}</div>
                  <div><span className="text-gray-500">Taux de roulement:</span> {operations.employeeTurnoverRate ? `${operations.employeeTurnoverRate}%` : "—"}</div>
                  <div><span className="text-gray-500">Concentration client:</span> {operations.customerConcentration ?? "—"}%</div>
                  <div><span className="text-gray-500">Fournisseurs clés:</span> {operations.keySuppliers || "—"}</div>
                </div>
                {operations.dailyOperationsDescription && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">
                      {lang === "fr" ? "Description des opérations" : "Operations Description"}
                    </p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{operations.dailyOperationsDescription}</p>
                  </div>
                )}
                {(operations.ownerCriticalTasks || operations.ownerAbsenceImpact) && (
                  <div className="mt-3 grid grid-cols-1 gap-2">
                    {operations.ownerCriticalTasks && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Tâches critiques du propriétaire" : "Owner critical tasks"}</p>
                        <p className="text-sm text-gray-200 whitespace-pre-wrap">{operations.ownerCriticalTasks}</p>
                      </div>
                    )}
                    {operations.ownerAbsenceImpact && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Impact de l'absence du propriétaire" : "Impact if owner absent"}</p>
                        <p className="text-sm text-gray-200 whitespace-pre-wrap">{operations.ownerAbsenceImpact}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {licensing && Object.keys(licensing).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "3. Licences et certifications" : "3. Licenses & Certifications"}
                </h3>
                {Array.isArray(licensing.licenses) && licensing.licenses.length > 0 ? (
                  <GenericItemList items={licensing.licenses} />
                ) : (
                  <p className="text-sm text-gray-500 mb-2">
                    {lang === "fr" ? "Aucune licence structurée soumise." : "No structured license entries submitted."}
                  </p>
                )}
                <div className="grid grid-cols-1 gap-2 mt-3 text-sm">
                  <div><span className="text-gray-500">Licences spéciales requises:</span> {yn(licensing.specialLicensesRequired, lang)}</div>
                  {licensing.certifications && (
                    <div><span className="text-gray-500">Certifications:</span> <span className="text-gray-200 whitespace-pre-wrap">{licensing.certifications}</span></div>
                  )}
                  {licensing.permits && (
                    <div><span className="text-gray-500">Permis:</span> <span className="text-gray-200 whitespace-pre-wrap">{licensing.permits}</span></div>
                  )}
                  {licensing.regulatoryRequirements && (
                    <div><span className="text-gray-500">Exigences réglementaires:</span> <p className="text-gray-200 whitespace-pre-wrap mt-1">{licensing.regulatoryRequirements}</p></div>
                  )}
                </div>
              </div>
            )}

            {assets && Object.keys(assets).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "4. Actifs" : "4. Assets"}
                </h3>
                <div className="grid grid-cols-3 gap-3 text-sm text-gray-200">
                  <div><span className="text-gray-500">Inventaire:</span> {fmt(assets.inventoryValue)}</div>
                  <div><span className="text-gray-500">FF&E:</span> {fmt(assets.ffeValue)}</div>
                  <div><span className="text-gray-500">Comptes recevables:</span> {fmt(assets.accountsReceivable)}</div>
                  <div><span className="text-gray-500">Encaisse:</span> {fmt(assets.cashEquivalents)}</div>
                  <div><span className="text-gray-500">Actifs totaux:</span> {fmt(assets.totalAssets)}</div>
                  <div><span className="text-gray-500">Passif total:</span> {fmt(assets.totalLiabilities)}</div>
                </div>
                {assets.assetNotes && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Notes sur les actifs" : "Asset notes"}</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{assets.assetNotes}</p>
                  </div>
                )}
              </div>
            )}

            {realEstate && Object.keys(realEstate).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "5. Immobilier" : "5. Real Estate"}
                </h3>
                {Array.isArray(realEstate.properties) && realEstate.properties.length > 0 ? (
                  <GenericItemList items={realEstate.properties} />
                ) : (
                  <div className="grid grid-cols-2 gap-3 text-sm text-gray-200">
                    <div><span className="text-gray-500">Possède immobilier:</span> {yn(realEstate.ownsRealEstate, lang)}</div>
                    <div><span className="text-gray-500">Valeur propriété:</span> {fmt(realEstate.propertyValue)}</div>
                    <div className="col-span-2"><span className="text-gray-500">Conditions de bail:</span> {realEstate.leaseTerms || "—"}</div>
                    <div><span className="text-gray-500">Inclus dans la vente:</span> {yn(realEstate.isIncluded, lang)}</div>
                    <div><span className="text-gray-500">Loyer mensuel:</span> {realEstate.monthlyRent ? fmt(realEstate.monthlyRent) : "—"}</div>
                  </div>
                )}
              </div>
            )}

            {financials && Object.keys(financials).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "6. Financiers" : "6. Financials"}
                </h3>
                <div className="grid grid-cols-3 gap-3 text-sm text-gray-200">
                  <div><span className="text-gray-500">Prix demandé:</span> {fmt(askingPrice)}{financials.includeRealEstateInPrice && <span className="text-xs text-amber-400 ml-1">(incl. immobilier)</span>}</div>
                  <div><span className="text-gray-500">Revenu An 1:</span> {fmt(financials.revenueYear1)}</div>
                  <div><span className="text-gray-500">Revenu An 2:</span> {fmt(financials.revenueYear2)}</div>
                  <div><span className="text-gray-500">Revenu An 3:</span> {fmt(financials.revenueYear3)}</div>
                  <div><span className="text-gray-500">EBITDA An 1:</span> {fmt(financials.ebitdaYear1)}</div>
                  <div><span className="text-gray-500">EBITDA An 2:</span> {fmt(financials.ebitdaYear2)}</div>
                  <div><span className="text-gray-500">EBITDA An 3:</span> {fmt(financials.ebitdaYear3)}</div>
                  <div><span className="text-gray-500">Bénéfice brut An 3:</span> {fmt(financials.grossProfitYear3)}</div>
                  <div><span className="text-gray-500">Revenu net An 3:</span> {fmt(financials.netIncomeYear3)}</div>
                  <div><span className="text-gray-500">Cash flow / SDE:</span> {fmt(financials.cashFlowEquity)}</div>
                  <div><span className="text-gray-500">Devise:</span> {financials.currency || "—"}</div>
                </div>
                {financials.askingPriceRationale && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Justification du prix" : "Asking price rationale"}</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{financials.askingPriceRationale}</p>
                  </div>
                )}
              </div>
            )}

            {addbackItems.length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "7. Retraitements (Addbacks)" : "7. Addbacks"}
                </h3>
                <table className="w-full text-sm">
                  <tbody>
                    {addbackItems.map((it, i) => (
                      <tr key={i} className="border-b border-gray-700 last:border-b-0">
                        <td className="py-1.5 text-gray-300">{it.description || "—"}</td>
                        <td className="py-1.5 text-right text-gray-200 font-medium">{fmt(it.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="py-2 font-semibold text-gray-200">{lang === "fr" ? "Total" : "Total"}</td>
                      <td className="py-2 text-right font-semibold text-gray-200">{fmt(addbacksTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {projections && Object.keys(projections).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "8. Projections" : "8. Projections"}
                </h3>
                <div className="grid grid-cols-3 gap-3 text-sm text-gray-200">
                  <div><span className="text-gray-500">Revenu projeté An 1:</span> {fmt(projections.revenueYear1)}</div>
                  <div><span className="text-gray-500">Revenu projeté An 2:</span> {fmt(projections.revenueYear2)}</div>
                  <div><span className="text-gray-500">Revenu projeté An 3:</span> {fmt(projections.revenueYear3)}</div>
                  <div><span className="text-gray-500">EBITDA projeté An 1:</span> {fmt(projections.ebitdaYear1)}</div>
                  <div><span className="text-gray-500">EBITDA projeté An 2:</span> {fmt(projections.ebitdaYear2)}</div>
                  <div><span className="text-gray-500">EBITDA projeté An 3:</span> {fmt(projections.ebitdaYear3)}</div>
                </div>
                {projections.growthAssumptions && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Hypothèses de croissance" : "Growth assumptions"}</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{projections.growthAssumptions}</p>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <p className="text-sm text-gray-200">
                    <span className="text-gray-500">{lang === "fr" ? "Financement vendeur:" : "Seller financing:"}</span>{" "}
                    {yn(projections.sellerFinancingOffered, lang)}
                    {projections.sellerFinancingOffered && (
                      <span className="text-gray-400">
                        {" — "}{fmt(projections.sellerFinancingAmount)}, {projections.sellerFinancingInterestRate}%, {projections.sellerFinancingTermMonths} {lang === "fr" ? "mois" : "months"}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {culture && Object.keys(culture).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "9. Culture et transition" : "9. Culture & Transition"}
                </h3>
                {Array.isArray(culture.coreValues) && culture.coreValues.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Valeurs fondamentales" : "Core values"}</p>
                    <ul className="text-sm text-gray-200 list-disc list-inside space-y-0.5">
                      {culture.coreValues.map((v: string, i: number) => (
                        <li key={i}>{v}{culture.coreValueDescriptions?.[i] ? ` — ${culture.coreValueDescriptions[i]}` : ""}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm text-gray-200">
                  <div><span className="text-gray-500">Équipe de gestion:</span> {culture.managementTeam || "—"}</div>
                  <div><span className="text-gray-500">Consultation post-vente:</span> {culture.postSaleConsultingAvailability || "—"}</div>
                  <div><span className="text-gray-500">Durée de transition:</span> {culture.transitionDurationPreference || "—"}</div>
                  <div><span className="text-gray-500">Période non-concurrence:</span> {culture.nonCompetePeriod || "—"}</div>
                </div>
                {culture.managementTeamExplanation && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Détails équipe de gestion" : "Management team details"}</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{culture.managementTeamExplanation}</p>
                  </div>
                )}
                {culture.postSaleConsultingDetails && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Détails consultation post-vente" : "Post-sale consulting details"}</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{culture.postSaleConsultingDetails}</p>
                  </div>
                )}
                {culture.idealSuccessorDescription && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Profil du successeur idéal" : "Ideal successor profile"}</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{culture.idealSuccessorDescription}</p>
                  </div>
                )}
                {culture.dealBreakersFromSeller && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">{lang === "fr" ? "Conditions non négociables du vendeur" : "Seller's deal breakers"}</p>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{culture.dealBreakersFromSeller}</p>
                  </div>
                )}
              </div>
            )}

            {swot && Object.keys(swot).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "10. Analyse SWOT" : "10. SWOT Analysis"}
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-gray-500 mb-1">Forces</p><p className="text-gray-200 whitespace-pre-wrap">{swot.strengths || "—"}</p></div>
                  <div><p className="text-xs text-gray-500 mb-1">Faiblesses</p><p className="text-gray-200 whitespace-pre-wrap">{swot.weaknesses || "—"}</p></div>
                  <div><p className="text-xs text-gray-500 mb-1">Opportunités</p><p className="text-gray-200 whitespace-pre-wrap">{swot.opportunities || "—"}</p></div>
                  <div><p className="text-xs text-gray-500 mb-1">Menaces</p><p className="text-gray-200 whitespace-pre-wrap">{swot.threats || "—"}</p></div>
                </div>
              </div>
            )}

            {impact && Object.keys(impact).length > 0 && (
              <div className={sectionCardClass}>
                <h3 className={sectionTitleClass}>
                  {lang === "fr" ? "Impact et main-d'œuvre" : "Impact & Workforce"}
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm text-gray-200">
                  <div><span className="text-gray-500">Nombre d'employés:</span> {impact.employeeCount ?? "—"}</div>
                  <div><span className="text-gray-500">Tenure moyenne:</span> {impact.averageEmployeeTenureYears ?? "—"} {lang === "fr" ? "ans" : "years"}</div>
                  <div><span className="text-gray-500">Régime de retraite:</span> {yn(impact.hasRetirementPlan, lang)}</div>
                  <div><span className="text-gray-500">Accidents (3 ans):</span> {impact.workplaceAccidentsLast3yr ?? "—"}</div>
                  <div><span className="text-gray-500">Chaîne d'approvisionnement locale:</span> {impact.localSupplyChainPercent ?? "—"}%</div>
                  <div><span className="text-gray-500">Certification environnementale:</span> {yn(impact.hasEnvironmentalCertification, lang)}</div>
                  <div><span className="text-gray-500">Historique de litige:</span> {yn(impact.hasLitigationHistory, lang)}</div>
                  <div><span className="text-gray-500">Implication communautaire:</span> {yn(impact.communityInvolvement, lang)}</div>
                </div>
              </div>
            )}

            {!raw.general && (
              <div className="text-center py-8 text-gray-500 text-sm">
                {lang === "fr" ? "Aucune donnée de formulaire disponible pour ce dossier." : "No form data available for this deal."}
              </div>
            )}
          </TabsContent>

          <TabsContent value="documents" className="space-y-3 mt-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2">
                <FolderLock className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-gray-200">
                  {lang === "fr" ? "Deal Room (visible acheteur après NDA)" : "Deal Room (buyer-visible after NDA)"}
                </span>
              </div>
              <Badge variant="outline">
                {dealRoomCount} {lang === "fr" ? "document(s)" : "document(s)"}
              </Badge>
            </div>

            {uploadedDocuments.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                  {lang === "fr" ? "Documents soumis par le vendeur" : "Seller-Submitted Documents"} ({uploadedDocuments.length})
                </p>
                <div className="space-y-2">
                  {uploadedDocuments.map((docItem: any, idx: number) => {
                    const safeName = docItem.name.replace(/[^a-zA-Z0-9._-]/g, "_");
                    const inRoom = dealRoomPaths.has(safeName);
                    return (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-200">{docItem.name}</p>
                          <p className="text-xs text-gray-500">{docItem.mimeType}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" asChild>
                          <a href={docItem.url} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button
                          size="sm"
                          variant={inRoom ? "secondary" : "outline"}
                          disabled={inRoom || pushingDoc === docItem.name}
                          onClick={() => pushToDealRoom(docItem)}
                        >
                          {pushingDoc === docItem.name ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : inRoom ? (
                            <Check className="h-3 w-3 mr-1" />
                          ) : null}
                          {inRoom
                            ? (lang === "fr" ? "Dans le Deal Room" : "In Deal Room")
                            : (lang === "fr" ? "Ajouter au Deal Room" : "Add to Deal Room")}
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(raw.assets?.assetListDocuments || []).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2 mt-4">
                  {lang === "fr" ? "Registre d'actifs / inventaire" : "Asset Register / Inventory"} ({raw.assets.assetListDocuments.length})
                </p>
                <div className="space-y-2">
                  {raw.assets.assetListDocuments.map((docItem: any, idx: number) => {
                    const safeName = (docItem.name || "").replace(/[^a-zA-Z0-9._-]/g, "_");
                    const inRoom = dealRoomPaths.has(safeName);
                    return (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-200">{docItem.name}</p>
                          <p className="text-xs text-gray-500">{docItem.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" asChild>
                          <a href={docItem.url} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button
                          size="sm"
                          variant={inRoom ? "secondary" : "outline"}
                          disabled={inRoom || pushingDoc === docItem.name}
                          onClick={() => pushToDealRoom(docItem)}
                        >
                          {pushingDoc === docItem.name ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : inRoom ? (
                            <Check className="h-3 w-3 mr-1" />
                          ) : null}
                          {inRoom
                            ? (lang === "fr" ? "Dans le Deal Room" : "In Deal Room")
                            : (lang === "fr" ? "Ajouter au Deal Room" : "Add to Deal Room")}
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2 mt-4">
                {lang === "fr" ? "Documents générés" : "Generated Documents"}
              </p>
              {generatedDocs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  {lang === "fr" ? "Aucun document généré." : "No generated documents."}
                </p>
              ) : (
                generatedDocs.map((docItem) => (
                  <div key={docItem.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg mb-2">
                    <div>
                      <p className="text-sm text-gray-200">{docItem.document_type?.toUpperCase()}</p>
                      <p className="text-xs text-gray-500">
                        {docItem.createdAt?.toDate?.()?.toLocaleDateString("fr-CA") || "—"}
                      </p>
                    </div>
                    <Badge variant="secondary">{docItem.status}</Badge>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </RequireStaff>
  );
}